// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { FormChangePassword } from '~/components/module/profile';
import { BaseInput, BaseInputPhone, BaseToolbar } from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { formatPhoneNumber, getInitials } from '~/utils/helper/common';
import { IFormProfile } from '~/utils/interface/user';
import { defaultValuesForm, schema } from './helper';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { getProfile, updateProfile } from '~/thunks/user/userThunk';
// Styles, images, icons
import styles from './Profile.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const Profile = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/profile', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IFormProfile>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  //#endregion Declare Hook

  //#region Selector
  const profile = useAppSelector((state) => state.user.profile);
  //#endregion Selector

  //#region Declare State
  const [selectedImage, setSelectedImage] = useState<string>(EMPTY_STRING);
  const [isShowChangePassword, setIsShowChangePassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!profile) return;

    handleResetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (data: IFormProfile) => {
    const formData = new FormData();
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    if (data.phoneNumber) formData.append('phoneNumber', data.phoneNumber);
    if (data.isDeleteAvatar) formData.append('deleteAvatar', 'true');
    if (data.avatarImage) formData.append('avatarImage', data.avatarImage);

    setIsLoading(true);
    dispatch(updateProfile(formData))
      .unwrap()
      .then(() => {
        dispatch(getProfile());
        setSelectedImage(EMPTY_STRING);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetForm = () => {
    reset({
      ...defaultValuesForm,
      email: profile.email || EMPTY_STRING,
      firstName: profile.firstName || EMPTY_STRING,
      lastName: profile.lastName || EMPTY_STRING,
      phoneNumber: profile.phoneNumber ? formatPhoneNumber(profile.phoneNumber) : EMPTY_STRING,
    });
  };

  const handleSelectImage = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedImage = e.target.files?.[DEFAULT_NUMBER_ZERO];

    if (selectedImage) {
      setValue('isDeleteAvatar', false);
      setValue('avatarImage', selectedImage, { shouldDirty: true, shouldValidate: true });
      setSelectedImage(URL.createObjectURL(selectedImage));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = EMPTY_STRING;
    }
  };

  const handleRemovePicture = () => {
    setValue('avatarImage', null);
    setSelectedImage(EMPTY_STRING);
    setValue('isDeleteAvatar', true);
  };

  const handleCancelForm = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleShowChangePassword = () => {
    setIsShowChangePassword(!isShowChangePassword);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('profile_title')}>
    <Box component={'div'} id='profilePage' className={cx('container')}>
      <BaseToolbar />

      <Box component={'div'} className={cx('body')}>
        <Stack
          component={'form'}
          spacing={'20px'}
          className={cx('form')}
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <Box component={'div'} className={cx('formBody')}>
            <Stack direction='row' alignItems='center' component={'div'} gap={'12px'}>
              <Box component={'div'} className={cx('avatarWrap')}>
                <Avatar
                  className={cx('avatar')}
                  src={selectedImage || profile?.avatarImage}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                >
                  {!selectedImage && !profile?.avatarImage && (
                    <Typography className={cx('avatarUserName')}>
                      {getInitials({ firstName: profile?.firstName, lastName: profile?.lastName })}
                    </Typography>
                  )}
                </Avatar>

                {selectedImage && (
                  <img
                    className={cx('iconRemoveImage')}
                    src={icons.commonIconClose}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    onClick={handleRemovePicture}
                  />
                )}
              </Box>

              <Stack component={'div'} gap={'12px'}>
                <Button variant='outlined' component='label'>
                  {t('field_select_image_label')}
                  <input
                    ref={fileInputRef}
                    hidden
                    accept='image/*'
                    type='file'
                    onChange={handleSelectImage}
                  />
                </Button>

                <Button variant='contained' onClick={handleShowChangePassword}>
                  {t('field_change_password_label')}
                </Button>
              </Stack>
            </Stack>

            <Grid2 container gridColumn={2} rowSpacing={'24px'} columnSpacing={'24px'}>
              <Grid2 size={6}>
                <Controller
                  control={control}
                  name='firstName'
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInput
                        value={value}
                        onChange={onChange}
                        required
                        label={t('field_first_name_label')}
                        errorMessage={errors.firstName?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  control={control}
                  name='lastName'
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInput
                        value={value}
                        onChange={onChange}
                        required
                        label={t('field_last_name_label')}
                        errorMessage={errors.lastName?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  control={control}
                  name='phoneNumber'
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInputPhone
                        value={value}
                        onChange={onChange}
                        label={t('field_phone_number_label')}
                        errorMessage={errors.phoneNumber?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  control={control}
                  name='email'
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInput
                        value={value}
                        onChange={onChange}
                        label={t('field_email_label')}
                        disabled
                        errorMessage={errors.email?.message}
                      />
                    );
                  }}
                />
              </Grid2>
            </Grid2>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCancelForm}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={!isDirty || isLoading}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {isShowChangePassword && <FormChangePassword onClose={handleShowChangePassword} />}
    </Box>
    </PageShell>
  );
};

export default Profile;
